import React, { useContext, useEffect, useRef } from "react";
import SaitGrid from "../atomic/SaitGrid";
import SaitFilters from "../atomic/SaitFilters";
import SaitButton from "../atomic/SaitButton";
import Box from "@mui/material/Box";
import { axiosGetForGrid } from "../utils/AxiosUtils";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setGlobalError } from "../store/globalMessageReducer";
import { EQUAL } from "../utils/FiltersOperators";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import Typography from "@mui/material/Typography";
import { ModuleContext } from "../utils/Constants";

export default function FiltersAndGrid(props) {
	const dispatch = useDispatch();
	const module = useContext(ModuleContext);

	const token = useSelector((state) => state["auth"]["token"]);

	const pageSize = props.gridParams.pageSize || 25;

	const [isFiltering, setIsFiltering] = React.useState(false);
	const [rows, setRows] = React.useState([]);
	const [totalCount, setTotalCount] = React.useState(0);
	const [jsonFilter, setJsonFilter] = React.useState([]);
	const [page, setPage] = React.useState(0);
	const [orderBy, setOrderBy] = React.useState([]);
	const [autoLoadFromProps, setAutoLoadFromProps] = React.useState(true);
	const [lastJsonFilter, setLastJsonFilter] = React.useState([]);
	const [resetFilters, setResetFilters] = React.useState(false);
	const [gridFilters, setGridFilters] = React.useState([]);

	// const [filtersReady, setFiltersReady] = React.useState(false);
	const filtersReady = useRef(false);
	const didMountRef = useRef(false);

	const componentModule = useContext(ModuleContext);

	const filtersId = props.gridId || props.gridParams.findFunction ? "FLTR_" + (props.gridParams.gridId || componentModule + props.gridParams.findFunction) : null;

	const getSavedFiltersFromCache = () => {
		const savedFiltersString = localStorage.getItem(filtersId);
		let savedFilters = [];
		if (savedFiltersString && savedFiltersString !== "undefined") {
			try {
				savedFilters = JSON.parse(savedFiltersString);
			} catch (error) {
				savedFilters = [];
			}
		}

		return savedFilters;
	};

	const [savedFilters, setSavedFilters] = React.useState(getSavedFiltersFromCache());
	const [activateSaveFileters, setActivateSaveFileters] = React.useState(false);
	const [newReportName, setNewReportName] = React.useState("");
	const [savedGridFilters, setSavedGridFilters] = React.useState(null);

	const { t } = useTranslation();

	useEffect(() => {
		if (didMountRef?.["isFiltering"]) {
			if (isFiltering) {
				fetchData();
			}
		} else {
			didMountRef["isFiltering"] = true;
		}
		// eslint-disable-next-line
	}, [isFiltering]);

	useEffect(() => {
		if (didMountRef?.["page"]) {
			setIsFiltering(true);
		} else {
			didMountRef["page"] = true;
		}
	}, [page]);

	useEffect(() => {
		if (didMountRef?.["orderBy"]) {
			handlePageChanged(0);
		} else {
			didMountRef["orderBy"] = true;
		}
		// eslint-disable-next-line
	}, [orderBy]);

	// La coppia [cleanGridFromProps, setCleanGridFromProps] mi permette di comandare lo
	// svuotamento della griglia dall'esterno
	useEffect(() => {
		if (props.cleanGridFromProps) {
			setRows([]);
			setTotalCount(0);
			props.setCleanGridFromProps(false);
		}
		// eslint-disable-next-line
	}, [props.cleanGridFromProps]);

	useEffect(() => {
	if (props.resetFilters) {
	setResetFilters(true);
	props.setResetFilters(false);
	}
	// eslint-disable-next-line
	}, [props.resetFilters]);

	const fetchData = async () => {
		// se la funzione beforeFilter è definita e ritorna true o se non è definita, la griglia carica i dati da db
		const beforeLoad = props.beforeFilter ? props.beforeFilter() : true;

		if (beforeLoad) {
			if (checkRequiredFilters(jsonFilter)) {
				if (props.clientSideFiltering) {
					const orderByArray = orderBy.concat(props.gridParams.orderBy);
					const result = await axiosGetForGrid(props.gridParams.findFunction, jsonFilter, orderByArray, 10000, page, token, module, dispatch, props.customAxiosInstance);
					props.setExternalRows?.(result.rows);

					setIsFiltering(false);
					setLastJsonFilter(jsonFilter);

					if (props.autoReselect?.reselect) {
						const newSelectedRow = result.rows.filter((row) => {
							return props.autoReselect.primaryKey.every((pk) => row[pk] === props.autoReselect.selectedRecord?.[pk]);
						});
						if (newSelectedRow.length === 1) {
							props.autoReselect.selectFunction(newSelectedRow[0]);
						}
					}
					props.setControlledFilter?.(false);
					props.afterFilter?.(result.rows);
				} else {
					const orderByArray = orderBy.concat(props.gridParams.orderBy);
					const result = await axiosGetForGrid(props.gridParams.findFunction, jsonFilter, orderByArray, pageSize, page, token, module, dispatch, props.customAxiosInstance);
					setRows(result.rows);
					setTotalCount(result.totalCount);
					setIsFiltering(false);
					setLastJsonFilter(jsonFilter);

					if (props.autoReselect?.reselect) {
						const newSelectedRow = result.rows.filter((row) => {
							return props.autoReselect.primaryKey.every((pk) => row[pk] === props.autoReselect.selectedRecord?.[pk]);
						});
						if (newSelectedRow.length === 1) {
							props.autoReselect.selectFunction(newSelectedRow[0]);
						}
					}
					props.setExternalRows?.(result.rows);
					props.setControlledFilter?.(false);
					props.afterFilter?.(result.rows);
				}
			} else {
				setIsFiltering(false);
				props.setControlledFilter?.(false);

				let requiredErrorString = t("insert_all_required_filters");

				if (props.filters.some((filter) => filter.required === true)) {
					requiredErrorString += "\n" + t("required_filters") + ": ";
					props.filters?.forEach((filter) => {
						if (filter.required === true) {
							requiredErrorString += filter.label + ", ";
						}
					});
					requiredErrorString = requiredErrorString.slice(0, -2);
				}

				props.combinedRequiredFilters?.forEach((combinedRequiredFilter) => {
					requiredErrorString += "\n" + t("alternative_filters") + ": ";

					combinedRequiredFilter?.forEach((requiredFilter) => {
						requiredErrorString += requiredFilter.label + ", ";
					});
					requiredErrorString = requiredErrorString.slice(0, -2);
				});

				dispatch(
					setGlobalError({
						module: module,
						errorMessage: requiredErrorString,
					})
				);
			}
		} else {
			setIsFiltering(false);
			props.setControlledFilter?.(false);
		}
	};

	const fetchDataForExcel = async (pageSizeParameter, pageParameter) => {
		if (rows?.length > 0) {
			if (checkRequiredFilters(jsonFilter)) {
				const result = await axiosGetForGrid(
					props.gridParams.findFunction,
					lastJsonFilter,
					orderBy.length > 0 ? orderBy : props.gridParams.orderBy,
					pageSizeParameter,
					pageParameter,
					token,
					module,
					dispatch,
					props.customAxiosInstance
				);

				return result;
			} else {
				let requiredErrorString = t("insert_all_required_filters");

				if (props.filters.some((filter) => filter.required === true)) {
					requiredErrorString += "\n" + t("required_filters") + ": ";
					props.filters?.forEach((filter) => {
						if (filter.required === true) {
							requiredErrorString += filter.label + ", ";
						}
					});
					requiredErrorString = requiredErrorString.slice(0, -2);
				}

				props.combinedRequiredFilters?.forEach((combinedRequiredFilter) => {
					requiredErrorString += "\n" + t("alternative_filters") + ": ";

					combinedRequiredFilter?.forEach((requiredFilter) => {
						requiredErrorString += requiredFilter.label + ", ";
					});
					requiredErrorString = requiredErrorString.slice(0, -2);
				});

				dispatch(
					setGlobalError({
						module: module,
						errorMessage: requiredErrorString,
					})
				);
			}
		}
	};

	const checkRequiredFilters = (jsonFilters) => {
		let allRequiredOK = true;

		props.filters?.forEach((filter) => {
			if (filter.required === true) {
				if (Array.isArray(filter.name)) {
					filter.name.forEach((filterCombo) => {
						let comboOperator = filter.operator || EQUAL;
						if (!jsonFilters.some((jsonFilter) => jsonFilter.field === filterCombo.storeName && jsonFilter.operator === comboOperator)) {
							allRequiredOK = false;
							return; // Esco dal ciclo forEach
						}
					});
					if (!allRequiredOK) {
						return; // Esco dal ciclo forEach
					}
				} else {
					if (!jsonFilters.some((jsonFilter) => jsonFilter.field === filter.name && jsonFilter.operator === filter.operator)) {
						allRequiredOK = false;
						return; // Esco dal ciclo forEach
					}
				}
			}
		});

		if (allRequiredOK) {
			props.combinedRequiredFilters?.forEach((combinedRequiredFilter) => {
				let singleCombinedRequiredOK = false;
				combinedRequiredFilter?.forEach((requiredFilter) => {
					if (jsonFilters.some((jsonFilter) => jsonFilter.field === requiredFilter.name && jsonFilter.operator === requiredFilter.operator)) {
						singleCombinedRequiredOK = true;
						return; // Esco dal ciclo forEach combinedRequiredFilter
					}
				});

				if (!singleCombinedRequiredOK) {
					allRequiredOK = false;
					return; // Esco dal ciclo forEach combinedRequiredFilters
				}
			});
		}
		return allRequiredOK;
	};

	const handleFireFilter = async () => {
		handlePageChanged(0);
	};

	const handlePageChanged = async (newPage) => {
		if (newPage !== page) {
			setPage(newPage);
		} else {
			setIsFiltering(true);
		}
	};

	const handleSortChanged = async (newSort) => {
		let newSortArray = [];
		if (newSort[0]) {
			newSortArray.push({
				property: newSort[0].field,
				direction: newSort[0].sort,
			});
		}
		setOrderBy(newSortArray);
	};

	const impostaFiltri = async (newFilters) => {
		filtersReady.current = true;
		setJsonFilter(newFilters);
	};

	////////////////////////////////////// FILTRI SALVATI //////////////////////////////////////

	function saveNewFilterInCache(panelFilters) {
		const newFilter = {
			name: newReportName,
			filters: { panel: panelFilters, grid: gridFilters },
		};
		if (props.externalFilters) {
			newFilter.filters.external = props.externalFilters;
		}
		let newSavedFilters = savedFilters;
		newSavedFilters.push(newFilter);

		localStorage.setItem(filtersId, JSON.stringify(newSavedFilters));
		setSavedFilters(newSavedFilters);
		setActivateSaveFileters(false);
		setNewReportName("");
	}

	function deleteSavedFilter(savedFilterName) {
		const newSavedFilters = savedFilters.filter((filter) => filter.name !== savedFilterName);

		localStorage.setItem(filtersId, JSON.stringify(newSavedFilters));
		setSavedFilters(newSavedFilters);
	}

	function applySavedFilters(savedFilters) {
		setGridFilters(savedFilters.grid);
		setSavedGridFilters(savedFilters.panel);
		props.setExternalFilters?.(savedFilters.external);
	}

	useEffect(() => {
		if (newReportName) {
			if (filtersId) {
				if (!savedFilters.some((item) => item.name === newReportName)) {
					// Attivo la variabile per salvare i filtri nel SaitFilters, nella callback salverò quelli della griglia
					setActivateSaveFileters(true);
				} else {
					dispatch(
						setGlobalError({
							module: module,
							errorMessage: t("a_report_with_this_name_already_exists"),
						})
					);
				}
			} else {
				dispatch(
					setGlobalError({
						module: module,
						errorMessage: t("grid_without_id"),
					})
				);
			}
		}

		// eslint-disable-next-line
	}, [newReportName]);

	////////////////////////////////////// FINE FILTRI SALVATI //////////////////////////////////////

	useEffect(() => {
		if (autoLoadFromProps && filtersReady.current) {
			if (props.autoload) {
				setIsFiltering(true);
				setAutoLoadFromProps(false);
			} else {
				setAutoLoadFromProps(false);
			}
		}
		// eslint-disable-next-line
	}, [jsonFilter, filtersReady.current]);

	// useEffect(() => {
	// 	if (props.filterListener != null && filtersReady) {
	// 		handleFireFilter();
	// 	}
	// 	// eslint-disable-next-line
	// }, [props.filterListener]);

	useEffect(() => {
		if (props.controlledFilter && filtersReady.current) {
			setIsFiltering(true);
		}
		// eslint-disable-next-line
	}, [props.controlledFilter, filtersReady.current]);

	useEffect(() => {
		if (props.clientControlledFilter) {
			props.clientSideFiltering?.(setRows);
			props.setClientControlledFilter?.(false);
		}
		// eslint-disable-next-line
	}, [props.clientControlledFilter]);

	useEffect(() => {
		if (props.resetPageNumber) {
			if (page > 0) setPage(0);
			props.setResetPageNumber?.(false);
		}
		// eslint-disable-next-line
	}, [props.resetPageNumber]);

	return (
		<Box
			sx={{
				display: "flex",
				flexGrow: 1,
				flexDirection: "column",
				...props.sx,
			}}
		>
			{props.title && (
				<Typography
					align="center"
					sx={{
						fontWeight: "bold",
						fontSize: 15,
						pb: 1, //padding-bottom
					}}
				>
					{props.title}
				</Typography>
			)}
			<SaitFilters
				filters={props.filters}
				impostaFiltri={impostaFiltri}
				handleFireFilter={handleFireFilter}
				resetFilters={resetFilters}
				setResetFilters={setResetFilters}
				gridFilters={gridFilters}
				setGridFilters={setGridFilters}
				filterButton={
					props.inLineButtonFilter && props.filters?.some((filter) => filter.type != null) ? (
						<>
							<SaitButton loading={isFiltering} onClick={handleFireFilter} icon={<RefreshIcon />} disabled={props.disabled || props.disableFilterButton || false} />
							{props.resetFilter && (
								<SaitButton color="divider" loading={isFiltering} onClick={() => setResetFilters(true)} icon={<FilterAltOffIcon />} disabled={props.disableResetFilterButton} />
							)}
						</>
					) : null
				}
				activateSaveFileters={activateSaveFileters}
				setActivateSaveFileters={setActivateSaveFileters}
				saveNewFilterInCache={saveNewFilterInCache}
				savedGridFilters={savedGridFilters}
				setSavedGridFilters={setSavedGridFilters}
			/>
			{!props.inLineButtonFilter && props.filters?.some((filter) => filter.type != null) ? (
				<Box m={1} textAlign="center">
					<SaitButton text={t("filter_grid")} loading={isFiltering} onClick={handleFireFilter} endIcon={<RefreshIcon />} disabled={props.disabled || props.disableFilterButton} />
					{props.resetFilter && (
						<SaitButton
							text={t("reset")}
							color="divider"
							loading={isFiltering}
							onClick={() => setResetFilters(true)}
							endIcon={<FilterAltOffIcon />}
							sx={{ ml: 2 }}
							disabled={props.disableResetFilterButton}
						/>
					)}
				</Box>
			) : null}
			{props.inLineButtonFilter && <Box mt={1} />}

			<SaitGrid
				gridId={props.gridParams.gridId}
				topButtons={props.topButtons}
				columns={props.gridParams.columns}
				disableToolbarFilterButton={props.disabled || props.disableFilterButton || false}
				rows={rows}
				isFiltering={isFiltering || autoLoadFromProps}
				pageSize={pageSize}
				handlePageChanged={handlePageChanged}
				page={page}
				rowCount={totalCount}
				handleSortChanged={handleSortChanged}
				selection={props.gridParams.selection}
				setSelectedRecord={props.gridParams.setSelectedRecord}
				handleRowDoubleClick={props.handleRowDoubleClick}
				orderBy={props.gridParams.orderBy}
				height={props.height}
				noStandardToolbar={props.noStandardToolbar}
				minHeight={props.minHeight}
				fetchDataForExcel={fetchDataForExcel}
				selectFirstRow={props.selectFirstRow}
				changeGridFilters={setGridFilters}
				gridFilters={gridFilters}
				pivotFunction={props.pivotFunction}
				sortingMode={props.sortingMode}
				paginationMode={props.paginationMode}
				onCellClick={props.onCellClick}
				rowBackgroundColor={props.rowBackgroundColor}
				findFunction={props.gridParams.findFunction}
				filtersId={filtersId}
				savedFilters={savedFilters}
				deleteSavedFilter={deleteSavedFilter}
				applySavedFilters={applySavedFilters}
				newReportName={newReportName}
				setNewReportName={setNewReportName}
				rowHeight={props.rowHeight}
				handleFireFilter={handleFireFilter}
				afterEdit={props.afterEdit}
				disabled={props.disabled}
			/>
		</Box>
	);
}
